import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import {
  getOfficialOverview,
  isOfficialProduct,
} from '@/data/defaultProducts';
import { Card, CardSubtitle, CardTitle } from '@/components/Card';
import { DomainBadge } from '@/components/Badge';
import { DomainIcon } from '@/components/DomainIcon';
import { ErrorState } from '@/components/ErrorState';
import { HashDisplay } from '@/components/HashDisplay';
import { Spinner } from '@/components/LoadingState';
import { LineageTree } from '@/features/verify/LineageTree';
import { ForksTree } from '@/features/marketplace/ForksTree';
import { RatingStars } from '@/features/marketplace/RatingStars';
import { useMarketplaceDetail } from '@/hooks/useMarketplace';
import { useLocaleStore } from '@/stores/localeStore';
import {
  formatAbsoluteTime,
  formatNumber,
  normalizeDomain,
} from '@/utils/format';
import { canonicalJsonPreview } from '@/features/spark/components/ManifestPreview';

type Tab = 'overview' | 'contract' | 'lineage' | 'forks' | 'reviews';

export function MarketplaceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['marketplace', 'common', 'errors']);
  const locale = useLocaleStore((s) => s.locale);
  const query = useMarketplaceDetail(id);
  const [tab, setTab] = useState<Tab>('overview');

  if (query.isPending) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="mx-auto max-w-2xl">
        <ErrorState
          title={t('not_found.title', { ns: 'errors' })}
          body={t('not_found.body', { ns: 'errors' })}
          actions={
            <Link to="/marketplace" className="contents">
              <Button variant="secondary" size="sm">
                {t('detail.back')}
              </Button>
            </Link>
          }
        />
      </div>
    );
  }

  const { listing, reviews } = query.data;
  const normalized = normalizeDomain(listing.domain);
  const domainLabel =
    typeof listing.domain === 'string'
      ? t(`domain.${listing.domain}`, { ns: 'common' })
      : listing.domain.Custom;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <Link to="/marketplace" className="text-sm text-ink-muted hover:text-ink">
        {t('detail.back')}
      </Link>

      <header className="flex flex-col gap-5 rounded-xl border border-border bg-surface p-6 md:flex-row md:items-start">
        <span
          className={`flex h-14 w-14 items-center justify-center rounded-lg bg-domain-${normalized}-soft text-domain-${normalized}`}
        >
          <DomainIcon domain={normalized} className="h-7 w-7" />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <DomainBadge domain={normalized} label={domainLabel} />
            {listing.certification !== 'none' ? (
              <span className="inline-flex items-center rounded-full bg-spark-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-spark">
                {t('detail.certified', { level: t(`filters.cert.${listing.certification}`) })}
              </span>
            ) : null}
            <span className="font-mono text-[11px] text-ink-subtle">v{listing.version}</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">{listing.name}</h1>
          <p className="text-sm text-ink-muted">{listing.tagline}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink-subtle">
            <span>{t('detail.author', { name: listing.author_name })}</span>
            <span>
              <time
                dateTime={listing.published_at.iso}
                title={formatAbsoluteTime(listing.published_at.iso, locale)}
              >
                {formatAbsoluteTime(listing.published_at.iso, locale)}
              </time>
            </span>
            <span>{t('card.installs', { value: formatNumber(listing.installs, locale) })}</span>
            <span className="inline-flex items-center gap-1.5">
              <RatingStars value={listing.rating} />
              <span>
                {listing.rating.toFixed(1)} · {t('card.reviews', { value: listing.review_count })}
              </span>
            </span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <a
            href="mailto:shalev.yoni@gmail.com"
            className="inline-flex items-center justify-center rounded-md bg-spark px-4 py-2 text-sm font-semibold text-white hover:bg-spark/90 hover:no-underline"
          >
            Contact Us
          </a>
          {isOfficialProduct(listing.root_id) && (
            <Button
              variant="secondary"
              onClick={() =>
                navigate('/spark', {
                  state: {
                    prefill: {
                      name: listing.name,
                      description: listing.description,
                      domain: listing.domain,
                      contract: listing.contract,
                    },
                  },
                })
              }
              data-testid="use-this-product"
            >
              {t('detail.use_this_product')}
            </Button>
          )}
        </div>
      </header>

      <nav
        aria-label={listing.name}
        className="flex flex-wrap gap-1 border-b border-border"
      >
        {(['overview', 'contract', 'lineage', 'forks', 'reviews'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            aria-current={tab === k ? 'page' : undefined}
            className={
              tab === k
                ? 'border-b-2 border-spark px-3 py-2 text-sm font-medium text-ink'
                : 'border-b-2 border-transparent px-3 py-2 text-sm text-ink-muted hover:text-ink'
            }
          >
            {t(`detail.tabs.${k}`)}
          </button>
        ))}
      </nav>

      {tab === 'overview' ? (() => {
        const overview = getOfficialOverview(listing.root_id, locale);
        // Officials with a curated overview get the rich
        // pain/solution/how-it-works/who-for layout. Other listings
        // (officials without curated copy + future third-party
        // publications) keep the original tagline + description card.
        if (!overview) {
          return (
            <Card>
              <CardTitle>{listing.name}</CardTitle>
              <CardSubtitle>{listing.tagline}</CardSubtitle>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink">
                {listing.description}
              </p>
            </Card>
          );
        }
        return (
          <div className="flex flex-col gap-5">
            {/* Tagline / headline */}
            <Card>
              <CardTitle>{listing.name}</CardTitle>
              <CardSubtitle>{listing.tagline}</CardSubtitle>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink">
                {listing.description}
              </p>
            </Card>

            {/* Pain / Solution */}
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-danger">
                  {t('detail.overview.pain_label')}
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ink">
                  {overview.pain}
                </p>
              </Card>
              <Card>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-spark">
                  {t('detail.overview.solution_label')}
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ink">
                  {overview.solution}
                </p>
              </Card>
            </div>

            {/* How it works */}
            <Card>
              <CardTitle>{t('detail.overview.how_label')}</CardTitle>
              <ol className="mt-4 grid gap-3 sm:grid-cols-3">
                {overview.how_it_works.map((step, idx) => (
                  <li
                    key={idx}
                    className="flex flex-col gap-2 rounded-lg border border-border bg-canvas p-4"
                  >
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-spark-soft text-xs font-semibold text-spark">
                      {idx + 1}
                    </span>
                    <span className="text-sm leading-relaxed text-ink">{step}</span>
                  </li>
                ))}
              </ol>
            </Card>

            {/* Who for + Pricing + Demo */}
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardTitle>{t('detail.overview.who_label')}</CardTitle>
                <p className="mt-2 text-sm leading-relaxed text-ink">
                  {overview.who_for}
                </p>
              </Card>
              <Card>
                <CardTitle>{t('detail.overview.pricing_label')}</CardTitle>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href="mailto:shalev.yoni@gmail.com"
                    className="inline-flex items-center justify-center rounded-md bg-spark px-3 py-1.5 text-sm font-semibold text-white hover:bg-spark/90 hover:no-underline"
                  >
                    Contact Us
                  </a>
                  {overview.demo_persona ? (
                    <Link
                      to={`/playground?persona=${overview.demo_persona}`}
                      className="inline-flex items-center justify-center rounded-md border border-border bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:border-spark hover:text-spark"
                    >
                      {t('detail.overview.see_demo')}
                    </Link>
                  ) : null}
                </div>
              </Card>
            </div>
          </div>
        );
      })() : null}

      {tab === 'contract' ? (
        <Card>
          <CardTitle>{t('detail.tabs.contract')}</CardTitle>
          <CardSubtitle>{t('detail.contract_caption')}</CardSubtitle>
          <pre className="mt-3 max-h-[420px] overflow-auto rounded-md border border-border bg-canvas p-4 font-mono text-xs leading-relaxed text-ink">
            {canonicalJsonPreview(listing.contract)}
          </pre>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            <HashDisplay value={listing.author_public_key} />
          </div>
        </Card>
      ) : null}

      {tab === 'lineage' ? (
        <Card>
          <CardTitle>{t('detail.tabs.lineage')}</CardTitle>
          <div className="mt-3">
            <LineageTree lineage={listing.lineage} />
          </div>
        </Card>
      ) : null}

      {tab === 'forks' ? (
        <ForksTree
          listing_root_id={listing.root_id}
          parent_name={listing.name}
          parent_creator={listing.author_name}
          parent_revenue_usd={listing.installs * Math.max(listing.price_usd, 1)}
          onFork={() =>
            navigate('/spark', {
              state: {
                prefill: {
                  name: `${listing.name} (fork)`,
                  description: `Fork of "${listing.name}". ${listing.tagline}`,
                  domain: listing.domain,
                  contract: listing.contract,
                },
              },
            })
          }
        />
      ) : null}

      {tab === 'reviews' ? (
        <Card>
          <CardTitle>{t('detail.tabs.reviews')}</CardTitle>
          {reviews.length === 0 ? (
            <p className="mt-2 text-sm text-ink-muted">{t('detail.reviews_empty')}</p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {reviews.map((r) => (
                <li key={r.id} className="flex flex-col gap-1.5 py-3">
                  <div className="flex items-center gap-2">
                    <RatingStars value={r.rating} />
                    <span className="text-sm font-medium text-ink">{r.author_name}</span>
                    <span className="text-[11px] text-ink-subtle">
                      {formatAbsoluteTime(r.at.iso, locale)}
                    </span>
                  </div>
                  <p className="text-sm text-ink">{r.body}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}
    </div>
  );
}
